import { Overlay } from 'components/Overlay';
import * as React from 'react';
import styled from 'styled-components';
import { uploadCustomFont } from 'state/actions';
import { getStoreState, storeDispatch } from 'state/Store';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 24px 0;
`;

const Label = styled.span`
  margin-right: 40px;
`;

const Value = styled.span`
  font-size: 14px;
  opacity: 0.7;
`;

const ErrorView = styled.div`
  text-align: right;
  font-size: 12px;
  line-height: 16px;
  color: var(--bright-red);
  position: absolute;
  bottom: -18px;
  right: 0;
`;

const TTFWarning = styled.div`
  border: solid 1px #d4a72c66;
  background: #fff8c5;
  padding: 8px 16px;
  font-size: 13px;
  line-height: 20px;
  max-width: 452px;
  text-align: left;
  border-radius: 6px;
`;

interface Props {
  file: File;
  initialName: string;
}
export const UploadCustomFontModal: React.FC<Props> = props => {
  const { user } = getStoreState().app;
  const { file, initialName } = props;
  const [name, setName] = React.useState(initialName);
  const [isUploading, setIsUploading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const upload = React.useCallback(() => {
    setIsUploading(true);
    storeDispatch(uploadCustomFont(file, name));
  }, [file, name]);

  const onInputChanged = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
      const val = e.target.value.trim();
      if (val.length === 0) {
        setErrorMessage('Name is too short.');
        return;
      }
      const existingFontNames = (user?.customFonts ?? []).map(f => f.name);
      if (existingFontNames.includes(val)) {
        setErrorMessage('Name already used by one of your other fonts.');
        return;
      }
      setErrorMessage(undefined);
    },
    [user]
  );

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const extension = file.name.split('.').pop()?.toLowerCase();

  return (
    <Overlay title="Upload custom font">
      <Row style={{ marginTop: 32 }}>
        <Label>Unique name:</Label>
        <div style={{ width: 300, position: 'relative' }}>
          <input
            style={{
              width: '100%',
              fontSize: '16px',
              textAlign: 'right',
              lineHeight: '20px',
              padding: '4px 8px',
              outlineColor:
                errorMessage !== undefined ? 'var(--bright-red)' : '#285DD1'
            }}
            ref={inputRef}
            value={name}
            onChange={onInputChanged}
          />
          {errorMessage && <ErrorView>{errorMessage}</ErrorView>}
        </div>
      </Row>
      <Row>
        <Label>File:</Label>
        <Value>{file.name}</Value>
      </Row>
      {extension === 'ttf' && (
        <Row>
          <TTFWarning>
            ⚠️ Some TTF fonts might not work properly. If you have problems
            using this font, try converting it into a different format.{' '}
            <a
              href="https://stackoverflow.com/a/53423940/326074"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
            .
          </TTFWarning>
        </Row>
      )}
      <button
        className="primary large"
        onClick={isUploading ? undefined : upload}
        disabled={isUploading || errorMessage !== undefined}
        style={{ marginTop: 20 }}
      >
        {isUploading ? 'Uploading...' : 'Upload'}
      </button>
    </Overlay>
  );
};



// WEBPACK FOOTER //
// ./src/account/UploadCustomFontModal.tsx